import { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { errorMessage } from "../../../api/client";
import { reportsApi, type Gate } from "../../../api/reports";
import { PageHeader } from "../../../components/ComingSoon";
import { Button, TextInput } from "../../../components/ui";
import { LoadState, useLoad } from "../shared";

function SignedRow({ g, s, onDone }: { g: Gate; s: NonNullable<Gate["signoff"]>; onDone: () => void }) {
  const [reason, setReason] = useState("");
  const act = (p: Promise<unknown>) => p.then(() => { toast.success("Saved"); onDone(); }).catch((e) => toast.error(errorMessage(e)));
  return (
    <div className="space-y-2 text-xs">
      <p className="text-emerald-200" data-testid={`gate-signed-${g.key}`}>Signed by {s.signer_name} ({s.signer_role}) on {s.signed_date}{s.note ? ` – ${s.note}` : ""}</p>
      {s.revoke_request ? (
        <div className="flex flex-wrap items-center gap-3 text-amber-200">Revocation requested: {s.revoke_request.reason}. A second admin must confirm.
          <Button onClick={() => act(reportsApi.revokeConfirm(g.key))} data-testid={`gate-revoke-confirm-${g.key}`}>Confirm revocation</Button></div>
      ) : (
        <div className="grid gap-2 sm:grid-cols-[1fr_auto]">
          <TextInput placeholder="Reason to revoke (required)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid={`gate-revoke-reason-${g.key}`} />
          <Button onClick={() => act(reportsApi.revokeRequest(g.key, reason))} data-testid={`gate-revoke-request-${g.key}`}>Request revoke</Button>
        </div>
      )}
    </div>
  );
}

function History({ g }: { g: Gate }) {
  const old = (g.history ?? []).filter((h) => h.id !== g.signoff?.id);
  if (!old.length) return null;
  return <ul className="text-[11px] text-zinc-500" data-testid={`gate-history-${g.key}`}>{old.map((h) => <li key={h.id}>{h.status}: {h.signer_name} ({h.signer_role}) {h.signed_date}{h.revoke_request ? ` – revoked: ${h.revoke_request.reason}` : ""}</li>)}</ul>;
}

function SignoffForm({ g, onDone }: { g: Gate; onDone: () => void }) {
  const [f, setF] = useState({ signer_name: "", signer_role: "", signed_date: "", note: "" });
  const s = g.signoff;
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  const act = (p: Promise<unknown>) => p.then(() => { toast.success("Saved"); onDone(); }).catch((e) => toast.error(errorMessage(e)));
  if (s?.status === "SIGNED") return <SignedRow g={g} s={s} onDone={onDone} />;
  if (s?.status === "PENDING_CONFIRMATION")
    return (
      <div className="flex flex-wrap items-center gap-3 text-xs text-amber-200">
        Recorded: {s.signer_name} ({s.signer_role}), {s.signed_date}. A second admin must confirm.
        <Button onClick={() => act(reportsApi.confirm(g.key))} data-testid={`gate-confirm-${g.key}`}>Confirm sign-off</Button>
      </div>
    );
  return (
    <div className="grid gap-2 sm:grid-cols-2">
      <TextInput placeholder="Signer name" value={f.signer_name} onChange={set("signer_name")} data-testid={`gate-name-${g.key}`} />
      <TextInput placeholder="Role / company" value={f.signer_role} onChange={set("signer_role")} data-testid={`gate-role-${g.key}`} />
      <TextInput type="date" value={f.signed_date} onChange={set("signed_date")} data-testid={`gate-date-${g.key}`} />
      <TextInput placeholder="Note" value={f.note} onChange={set("note")} data-testid={`gate-note-${g.key}`} />
      <Button className="whitespace-nowrap" onClick={() => act(reportsApi.signoff(g.key, f))} data-testid={`gate-record-${g.key}`}>Record sign-off</Button>
    </div>
  );
}

export default function LaunchReadiness() {
  const [n, setN] = useState(0);
  const { data, error } = useLoad(useCallback(() => reportsApi.gates(), [n])); // eslint-disable-line react-hooks/exhaustive-deps
  const row = (g: Gate) => (
    <li key={g.key} className="space-y-2 border-t border-white/5 py-3" data-testid={`gate-${g.key}`}>
      <div className="flex items-center gap-3 text-sm">
        <span className={`font-mono text-base ${g.ok ? "text-emerald-300" : "text-red-300"}`} data-testid={`gate-status-${g.key}`}>{g.ok ? "✓" : "✗"}</span>
        <span className="text-white">{g.label}</span>
        {g.payroll && <span className="rounded-sm border border-white/15 px-1.5 text-[10px] uppercase text-zinc-400">payroll gate</span>}
      </div>
      {g.note && <p className="pl-7 text-xs text-sky-300" data-testid={`gate-note-text-${g.key}`}>{g.note}</p>}
      {g.kind === "MANUAL" && <div className="space-y-2 pl-7"><SignoffForm g={g} onDone={() => setN(n + 1)} /><History g={g} /></div>}
    </li>
  );
  return (
    <div data-testid="launch-readiness-page" className="animate-rise space-y-6">
      <PageHeader title="Launch readiness" />
      <Link to="/admin/settings" className="text-xs text-signal hover:text-white" data-testid="launch-back-link">← Settings</Link>
      <LoadState error={error} loading={!data && !error} testId="launch-load" />
      {data && (
        <>
          <p className={`rounded-sm border px-4 py-3 text-sm ${data.enforced ? "border-red-500/40 bg-red-500/10 text-red-100" : "border-white/15 bg-surface text-zinc-300"}`} data-testid="launch-mode-note">
            {data.app_env.toUpperCase()} · {data.note} {data.missing_payroll.length > 0 && `Missing payroll gates: ${data.missing_payroll.join(", ")}.`}
          </p>
          <div className="grid gap-8 lg:grid-cols-2">
            <section><p className="label-caps mb-2 text-zinc-500">Auto-detected</p><ul data-testid="launch-auto-gates">{data.gates.filter((g) => g.kind === "AUTO").map(row)}</ul></section>
            {data.hints?.filter((h) => !h.ok).map((h) => (
              <p key={h.key} className="rounded-sm border border-amber-400/30 bg-amber-400/10 px-4 py-3 text-sm text-amber-100" data-testid={`launch-hint-${h.key}`}>
                <span className="font-semibold">{h.label}.</span> {h.detail} <span className="text-amber-200/70">(Not a blocker.)</span></p>
            ))}
            <section><p className="label-caps mb-2 text-zinc-500">Manual sign-offs (two admins: record + confirm)</p><ul data-testid="launch-manual-gates">{data.gates.filter((g) => g.kind === "MANUAL").map(row)}</ul></section>
          </div>
          <p className="text-xs text-zinc-500">Do not deploy to production until legal/payroll review and these gates are satisfied.</p>
        </>
      )}
    </div>
  );
}
